import React, { useState } from 'react';
import { Form } from 'antd';
import { useDataProvider, useNotify, usePermissions, useRedirect } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import { CronInput, Field, FormSection, NetorcaForm, ToggleField, useServerErrors } from '../Common/Form';
import { EntityFormRoute, HttpRequestFields, ServiceField } from '../Common/Integrations';
import MainCard from '../Common/MainCard/MainCard';
import DeleteButton from '../Common/DeleteButton';
import HealthcheckHelp from './HealthcheckHelp';

const RESOURCE = 'serviceowner_healthchecks';

/** HealthCheckSerializer.Meta.fields */
const HEALTHCHECK_FIELDS = [
    'active',
    'schedule_crontab',
    'schedule_enabled',
    'service',
    'target_url',
    'authorization',
    'verify',
    'timeout',
];

const HealthcheckForm = ({ record }: { record?: any }) => {
    const [form] = Form.useForm();
    const { permissions } = usePermissions();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const { apply, fieldErrors, clear } = useServerErrors(form, HEALTHCHECK_FIELDS);
    const [saving, setSaving] = useState(false);

    const isAdmin = permissions === 'ADMIN';
    const editing = !!record;
    const scheduled = Form.useWatch('schedule_enabled', form);

    const onFinish = (values: any) => {
        clear();
        setSaving(true);

        const { schedule_enabled: enabled, schedule_crontab: crontab, ...rest } = values;
        // The API takes a PUT, so a field the form does not show still has to
        // travel with the payload. `active` is owned by the list page, which is
        // where a healthcheck is paused and resumed.
        const payload: any = { ...rest, active: editing ? record.active !== false : true };

        // The API reads the schedule as one nested object, and the crontab is
        // what tells it to resolve one:
        //   scheduling on            → send the expression and the flag
        //   turning an existing one off → send a null expression, which deletes it
        //   never had one           → send neither key
        if (enabled) {
            payload.schedule_enabled = true;
            // A crontab in the payload tells the serializer to re-resolve the
            // schedule, so only send one the user actually changed.
            if (crontab !== record?.schedule_crontab) payload.schedule_crontab = crontab;
        } else if (record?.schedule_crontab) {
            payload.schedule_crontab = null;
        }

        const request = editing
            ? dataProvider.update(RESOURCE, { id: record.id, data: payload, previousData: { id: record.id } })
            : dataProvider.create(RESOURCE, { data: payload });

        request
            .then(() => {
                notify(editing ? 'Healthcheck updated' : 'Healthcheck created');
                redirect(`/${RESOURCE}`);
            })
            .catch((error: any) => {
                apply(error, editing ? 'Could not update the healthcheck' : 'Could not create the healthcheck');
                setSaving(false);
            });
    };

    return (
        <NetorcaForm
            className="nf-form--sheet"
            form={form}
            name="healthcheck"
            /* no eyebrow: the arrow beside the name already says what list it
               belongs to, and the name is what the page is about */
            title={editing ? `${record.service?.name ?? 'Service'} healthcheck` : 'New healthcheck'}
            onBack={() => redirect(`/${RESOURCE}`)}
            backLabel="Back to healthchecks"
            help={{ title: 'How healthchecks work', content: <HealthcheckHelp /> }}
            readOnly={!isAdmin}
            submitLabel={editing ? 'Save changes' : 'Create healthcheck'}
            submitting={saving}
            onCancel={() => redirect(`/${RESOURCE}`)}
            onFinish={onFinish}
            serverErrors={fieldErrors}
            destructive={
                editing && isAdmin ? <DeleteButton title="Healthcheck" resource={RESOURCE} record={record} /> : undefined
            }
            initialValues={
                record
                    ? { ...record, service: record.service?.id, schedule_enabled: !!record.schedule_enabled }
                    : {
                          verify: true,
                          timeout: 20,
                          schedule_enabled: false,
                          schedule_crontab: '0 * * * *',
                      }
            }
        >
            {/* One grid for the whole form, read top to bottom: the service it
                probes and where it calls, then when it runs. Widths follow
                what each control actually holds, and every row is filled, so
                the order alone carries the grouping and nothing has to rule
                it off. */}
            <FormSection id="healthcheck" title="Healthcheck" bare>
                <ServiceField
                    span={4}
                    // the API rejects a change of service on an existing healthcheck
                    locked={editing}
                    hint={editing ? 'Fixed once the healthcheck exists.' : 'Every in-service item is checked.'}
                />

                {/* the service takes the first third of the row, so the URL
                    takes the rest of it rather than a row of its own */}
                <HttpRequestFields urlSpan={8} />

                <ToggleField
                    name="schedule_enabled"
                    span={12}
                    label="Run on a schedule"
                    hint="With this off, the healthcheck runs only when someone triggers it."
                />

                {scheduled && (
                    <Field
                        name="schedule_crontab"
                        label="Runs at"
                        hint="Build the schedule below or type a five-field cron expression directly."
                        rules={[
                            { required: true, message: 'Set when the healthcheck should run' },
                            {
                                validator: async (_, value) => {
                                    if (!value) return;
                                    if (String(value).trim().split(/\s+/).length !== 5) {
                                        throw new Error('A cron expression needs exactly five fields');
                                    }
                                },
                            },
                        ]}
                    >
                        <CronInput />
                    </Field>
                )}
            </FormSection>
        </NetorcaForm>
    );
};

const HealthcheckCreate = (props: any) => (
    <EntityFormRoute
        id={props.id}
        resource={RESOURCE}
        basePath={`/${RESOURCE}`}
        render={(record) => (
            <MainCard>
                <HealthcheckForm record={record} />
            </MainCard>
        )}
    />
);

export default HealthcheckCreate;
